import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  CreditCard,
  Download,
  ExternalLink,
  Receipt,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const invoices = [
  {
    id: 1,
    date: "May 1, 2026",
    amount: "$49.00",
    status: "Paid",
  },
  {
    id: 2,
    date: "Apr 1, 2026",
    amount: "$49.00",
    status: "Paid",
  },
  {
    id: 3,
    date: "Mar 1, 2026",
    amount: "$49.00",
    status: "Paid",
  },
];

const includedFeatures = [
  "Google review syncing",
  "Prepared review responses",
  "Email approval workflow",
  "Review dashboard",
  "Tone customization",
  "Unlimited responses",
  "Reputation insights",
  "Basic analytics",
];

export default function DemoBilling() {
  const isFoundingCustomer = true;

  return (
    <div className="space-y-6">
      <header className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Billing
          </h1>

          <p className="text-slate-500 mt-1">
            Manage your subscription, payment method, and invoices.
          </p>
        </div>

        <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <ExternalLink size={16} />
          Open Stripe Portal
        </button>
      </header>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-start">
        <div className="xl:col-span-2 p-0 lg:p-4 lg:rounded-2xl lg:border lg:border-slate-200 lg:bg-white lg:shadow-sm">
          <div className="rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white p-6 md:p-8 overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.35),transparent_35%)]" />

            <div className="relative">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-indigo-100">
                  <ShieldCheck size={16} />
                  Current subscription
                </div>

                <span className="w-fit rounded-full bg-green-400/10 border border-green-400/20 px-3 py-1 text-xs font-semibold text-green-300">
                  Active
                </span>
              </div>

              <div className="mt-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">
                    RevUShield
                  </h2>

                  <p className="text-slate-300 mt-2 max-w-xl">
                    Complete review response management for each business
                    location.
                  </p>
                </div>

                {isFoundingCustomer && (
                  <div className="rounded-2xl border border-indigo-300/20 bg-white/10 px-4 py-3">
                    <div className="flex items-center gap-2 text-indigo-100 font-semibold text-sm">
                      <Sparkles size={16} />
                      Founding Customer
                    </div>

                    <p className="text-xs text-slate-300 mt-1">
                      Lifetime pricing locked in.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-8 flex items-end gap-2">
                <span className="text-5xl font-bold">
                  {isFoundingCustomer ? "$49" : "$79"}
                </span>

                <span className="text-slate-300 mb-2">/mo per location</span>
              </div>

              {isFoundingCustomer ? (
                <p className="text-sm text-indigo-100 mt-3">
                  Standard pricing is $79/mo. Your founding customer rate is
                  locked at $49/mo for the life of your subscription.
                </p>
              ) : (
                <p className="text-sm text-indigo-100 mt-3">
                  Additional locations are billed at the same monthly rate.
                </p>
              )}

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {includedFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <CheckCircle
                      size={18}
                      className="text-green-400 mt-0.5 shrink-0"
                    />

                    <span className="text-sm text-slate-100">{feature}</span>
                  </div>
                ))}
              </div>

              <button className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-medium text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20">
                Manage Subscription
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard size={20} className="text-indigo-600" />

              <h2 className="text-lg font-semibold text-slate-900">
                Payment Method
              </h2>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Managed securely by</p>

              <p className="text-2xl font-bold text-slate-900 mt-1">Stripe</p>

              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                RevUShield never stores or has access to your full credit card
                information.
              </p>
            </div>

            <button className="mt-4 w-full border border-slate-200 hover:bg-slate-100 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition">
              Update Payment Method
            </button>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2">
              <AlertCircle size={20} className="text-amber-600" />

              <h2 className="text-lg font-semibold text-slate-900">
                Trial Status
              </h2>
            </div>

            <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4">
              <h3 className="font-semibold text-amber-900">
                Trial ending soon
              </h3>

              <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                Your free trial ends in 4 days. Add a payment method to keep
                review responses running without interruption.
              </p>
            </div>
          </section>
        </aside>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Receipt size={20} className="text-indigo-600" />

            <h2 className="text-lg font-semibold text-slate-900">
              Billing History
            </h2>
          </div>

          <button className="w-fit text-sm font-medium text-indigo-600 hover:text-indigo-700">
            View All
          </button>
        </div>

        <div className="divide-y divide-slate-200">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div>
                <p className="font-medium text-slate-900">
                  Invoice — {invoice.date}
                </p>

                <p className="text-sm text-slate-500">{invoice.amount}</p>
              </div>

              <div className="flex items-center gap-3">
                <span className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                  {invoice.status}
                </span>

                <button className="border border-slate-200 hover:bg-slate-100 text-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
                  <Download size={15} />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-semibold text-white">
            Payments are processed by Stripe
          </h3>

          <p className="text-sm text-slate-300 mt-1">
            Your billing details, invoices, and payment method are managed
            securely through Stripe.
          </p>
        </div>

        <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition">
          Open Billing Portal
          <ExternalLink size={15} />
        </button>
      </section>
    </div>
  );
}
